import * as yup from 'yup';
import { formatUrl } from '@/lib/utils/string';
import isURL from 'validator/lib/isURL';
import isMailtoURI from 'validator/lib/isMailtoURI';
import { STORE_COMPONENTS } from '@/lib/constants';
import { store } from '@/redux/store';

export type IFormInputs = yup.InferType<ReturnType<typeof generateSchema>>;

/*
 * Generate the code schema.
 * This schema is used to validate the code form fields
 */
export const generateSchema = () => {
  const { object, array, string, boolean } = yup;

  return object({
    // target group
    targetGroup: string(),
    // communication purpose
    communicationPurpose: string().when('targetGroup', {
      is: (targetGroup: string) => targetGroup === 'customer',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    // subcategory
    subcategory: string().when('targetGroup', {
      is: (targetGroup: string) => targetGroup === 'customer',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    // Code settings
    targetUrl: string()
      .required('label.requiredUrl')
      .test(
        'validate-url',
        'page.createCode.messages.form.urlError',
        (value) => {
          if (typeof value !== 'string' || value.trim().length === 0) {
            return false;
          }

          // Allow mailto: URIs
          if (value.toLowerCase().startsWith('mailto:')) {
            return isMailtoURI(value);
          }

          return isURL(formatUrl(value), {
            protocols: ['http', 'https'],
            require_protocol: true,
          });
        }
      ),
    codeName: string().required('label.requiredName'),
    showIcon: boolean(),
    icon: string().when('showIcon', {
      is: (showIcon: boolean) => showIcon,
      then: (schema) => schema.required('label.requiredIcon'),
      otherwise: (schema) => schema.notRequired(),
    }),
    // Location
    defaultLocation: boolean(),
    location: object({
      country: string().required(),
      site: string().required(),
    }),
    onlyOnlineUsage: boolean(),
    store: object({
      component: array(object({ value: string() })),
      department: array(object({ value: string() })),
    }).when(['location.site', 'onlyOnlineUsage'], {
      is: (site: string, onlyOnlineUsage: boolean) => {
        // It should be a better way to obtain the query data without hooks :S
        const cachedQueries = store.getState().informeraApi.queries || {};
        const locations = (cachedQueries['getLocations(undefined)']?.data ||
          []) as { location: string }[];

        return locations.some((l) => l.location === site) && !onlyOnlineUsage;
      },
      then: () =>
        object({
          component: array(object({ value: string() })).min(
            1,
            'label.required'
          ),
          department: array(object({ value: string() })).when('component', {
            is: (selectedComponents: { value: string }[]) => {
              if (!selectedComponents) return false;
              const departments = STORE_COMPONENTS.filter((component) =>
                selectedComponents.some((c) => c.value === component.value)
              ).flatMap((component) => component.departments);
              return departments.length > 0;
            },
            then: (schema) => schema.min(1, 'label.required'),
            otherwise: (schema) => schema,
          }),
        }),
      otherwise: (schema) => schema,
    }),
    // Permissions
    permissions: string().required('label.required'),
    // Communication carrier
    carrier: object({
      type: array(object({ value: string() })),
      size: array(object({ value: string() })),
    }),
  });
};
